/**
 * "Confirm the spelling" — the review of server-library customer names that matched no CRM client (customerAliasReview, one doc per exact name,
 * seeded from the live libraries by _a10_b727alias_seed.cjs) and the engine that acts on a decision (mfxAliases, Cloud Function, us-central1).
 * The page READS the review straight from Firestore (rules: company users read, nobody writes); every decision goes through the engine, which
 * checks who you are, writes customers.aliases, relinks the records and logs it. Lane b727alias, 2026-10-26.
 */
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { auth, db } from './firebase';

export const ALIAS_URL = 'https://us-central1-mfx-2026.cloudfunctions.net/mfxAliases';

export type ReviewStatus = 'pending' | 'confirmed' | 'dismissed' | 'resolved';
export interface Suggestion { mfxId: string; customerId: string; name: string; score: number; }
export interface ReviewItem {
  key: string; name: string; status: ReviewStatus; total: number; counts: Record<string, number>;
  firstYear: number | null; lastYear: number | null; examples: { col: string; id: string; label: string }[]; suggestions: Suggestion[];
  mfxId?: string | null; customerId?: string | null; customerName?: string | null; decidedBy?: { email?: string } | null; decidedAt?: any;
  linkedTotal?: number | null; dismissReason?: string | null; autoLinked?: number;
}
export interface LogItem { id: string; action: string; name: string; mfxId?: string; customerName?: string; by?: { email?: string } | string; at?: any; linked?: number; unlinked?: number; reason?: string; }

export const COUNT_LABEL: [string, string][] = [
  ['blueprintLibrary', 'blueprints'], ['jobTicketLibrary', 'tickets'], ['customerPoFiles', 'POs'],
  ['capaLibrary', 'CAPA'], ['complaintLibrary', 'complaints'], ['fsqmsLibraryFiles', 'request files'],
];

export async function loadReview(): Promise<ReviewItem[]> {
  const snap = await getDocs(collection(db, 'customerAliasReview'));
  return snap.docs.map((d) => {
    const x = d.data() as any;
    return { key: d.id, name: String(x.name ?? ''), status: (x.status || 'pending') as ReviewStatus, total: Number(x.total) || 0, counts: x.counts || {},
      firstYear: x.firstYear ?? null, lastYear: x.lastYear ?? null, examples: Array.isArray(x.examples) ? x.examples : [], suggestions: Array.isArray(x.suggestions) ? x.suggestions : [],
      mfxId: x.mfxId ?? null, customerId: x.customerId ?? null, customerName: x.customerName ?? null, decidedBy: x.decidedBy ?? null, decidedAt: x.decidedAt ?? null,
      linkedTotal: x.linkedTotal ?? null, dismissReason: x.dismissReason ?? null, autoLinked: x.autoLinked ?? 0 };
  });
}
export async function loadLog(n = 40): Promise<LogItem[]> {
  const snap = await getDocs(query(collection(db, 'customerAliasLog'), orderBy('at', 'desc'), limit(n)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
}

export interface EngineReply { ok: boolean; error?: string; code?: string; [k: string]: any; }
export async function callEngine(body: Record<string, unknown>): Promise<EngineReply> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) return { ok: false, error: 'Sign in again.' };
  try {
    const res = await fetch(ALIAS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
    const j = await res.json().catch(() => ({}));
    return res.ok ? { ok: true, ...j } : { ok: false, error: j.error || `HTTP ${res.status}`, code: j.code };
  } catch (e: any) { return { ok: false, error: 'Network: ' + (e?.message || 'failed') }; }
}

/** the OS library pages resolve a name slug, so an unlinked record can still be opened (b718mid) */
export const nameSlug = (s: string) => String(s || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/&/g, ' and ').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'unfiled';

/** serverSync/_summary — the nightly server → OS check (mfxServerSync, b728sync) */
export interface SyncSummary { checkedAt?: any; libraries: { lib: string; label: string; state: string }[]; changed: string[]; problems: string[]; pendingNames?: number; autoLinked?: number; }
export async function loadSyncSummary(): Promise<SyncSummary | null> {
  const { doc, getDoc } = await import('firebase/firestore');
  const s = await getDoc(doc(db, 'serverSync', '_summary'));
  return s.exists() ? (s.data() as SyncSummary) : null;
}
export function syncLine(s: SyncSummary | null): { ok: boolean; text: string } | null {
  if (!s) return null;
  const d = s.checkedAt?.toDate ? s.checkedAt.toDate() : null; const when = d ? d.toLocaleString() : 'not yet';
  const changed = s.libraries.filter((l) => l.state === 'changed').map((l) => l.label);
  const bad = s.libraries.filter((l) => l.state === 'missing' || l.state === 'error').map((l) => l.label);
  if (bad.length) return { ok: false, text: `Server sync checked ${when}: could not read ${bad.join(', ')}.` };
  if (changed.length) return { ok: false, text: `Server sync checked ${when}: newer files on the server for ${changed.join(', ')} — not in the OS yet (re-import owed).` };
  return { ok: true, text: `Server sync checked ${when}: all ${s.libraries.length} libraries match the server.${s.autoLinked ? ` ${s.autoLinked} new record(s) linked by confirmed spellings.` : ''}` };
}
